/**
 * Competitor Source Finder Service
 * 
 * Uses Lovable AI via edge function to find real competitors
 * based on idea description and target customer.
 */

import { supabase } from '@/integrations/supabase/client';
import type {
  CompetitorSearchInput,
  CompetitorSearchOutput,
  ICompetitorSourceFinder,
} from './types';

/**
 * Live implementation using Lovable AI
 */
class LiveCompetitorSourceFinder implements ICompetitorSourceFinder {
  async findCompetitors(input: CompetitorSearchInput): Promise<CompetitorSearchOutput> {
    const { data, error } = await supabase.functions.invoke('analyze-competitors', {
      body: input,
    });

    if (error) {
      console.error('Competitor analysis error:', error);
      throw new Error(error.message || 'Failed to analyze competitors');
    }

    if (data?.error) {
      throw new Error(data.error);
    }

    // Validate and return the response
    if (!data?.competitors || !data?.insight) {
      throw new Error('Invalid response from analysis service');
    }

    return {
      competitors: data.competitors,
      insight: data.insight,
    };
  }
}

// Export singleton instance
export const CompetitorSourceFinder: ICompetitorSourceFinder = new LiveCompetitorSourceFinder();

// Export class for testing
export { LiveCompetitorSourceFinder };
